/**
 * Status pill.
 *
 * The previous version mapped eleven words to four variants and sent anything
 * else to `info`, so `maintenance`, `needs_migration`, `cancelled` and
 * `revoked` all rendered the same neutral blue regardless of severity.
 *
 * Colour now comes from `status-color`, which maps every backend enum
 * deliberately. Pass `vocabulary` where it is known — the same word means
 * different things across enums, and only the vocabulary disambiguates a
 * `pending` organization (waiting on a human) from a `pending` task (waiting
 * on a worker). Without it, a best-effort search keeps existing call sites
 * working until each is migrated.
 */

import { knownStatuses, statusColor, type StatusVocabulary } from '../lib/status-color.js';
import { Badge } from './finixt.js';

/** Search order matters: the first vocabulary containing the word wins. */
const SEARCH_ORDER: StatusVocabulary[] = [
  'organization',
  'task',
  'agentRuntime',
  'capability',
  'federatedPartner',
  'installJob',
  'document',
  'agentLifecycle',
  'taskSource',
  'wheelKind',
  'flag',
];

function guessVocabulary(status: string): StatusVocabulary {
  return SEARCH_ORDER.find((v) => knownStatuses(v).includes(status)) ?? 'organization';
}

export interface StatusBadgeProps {
  status: string;
  label?: string;
  /** Supply this once the call site is migrated; it removes the guess. */
  vocabulary?: StatusVocabulary;
}

export function StatusBadge({ status, label, vocabulary }: StatusBadgeProps) {
  const normalised = status.toLowerCase();
  const resolved = vocabulary ?? guessVocabulary(normalised);
  return <Badge tone={statusColor(resolved, normalised)}>{label ?? status}</Badge>;
}
